import React, { useState } from "react";
import { motion } from "framer-motion";
import { Clock, MapPin, Target, Award, ArrowRight, Calendar, Users, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Mock icons data with colors
const icons = [
  { image: "🚗", name: "Automotive", color: "bg-blue-500" },
  { image: "🏦", name: "Bank & Finance", color: "bg-green-500" },
  { image: "💻", name: "Technology", color: "bg-purple-500" },
  { image: "🏛️", name: "Government", color: "bg-red-500" },
  { image: "❤️", name: "Health Care", color: "bg-pink-500" },
  { image: "🛡️", name: "Insurance", color: "bg-orange-500" },
  { image: "🧬", name: "Life Science", color: "bg-cyan-500" },
  { image: "🏢", name: "Real Estate", color: "bg-indigo-500" },
  { image: "🛍️", name: "Retail", color: "bg-yellow-500" },
  { image: "📡", name: "Telecom", color: "bg-teal-500" },
];


const ProgramsCard = ({ program }) => {
  const [showSubPrograms, setShowSubPrograms] = useState(false);
  const hasMultiplePaths = program.paths && program.paths.length > 1;
  const navigate = useNavigate();

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    },
    hover: { 
      y: -8,
      transition: { duration: 0.3, ease: "easeInOut" }
    }
  };

  const imageVariants = {
    hover: { 
      scale: 1.05,
      transition: { duration: 0.4, ease: "easeInOut" }
    }
  };

  const iconVariants = {
    hover: { 
      scale: 1.1,
      rotate: 5,
      transition: { duration: 0.2 }
    }
  };

  const subProgramVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: { 
      opacity: 1, 
      height: "auto",
      transition: { duration: 0.4, ease: "easeInOut" }
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      className="group relative flex flex-col bg-white rounded-3xl shadow-2xl shadow-black/5 border border-gray-100 overflow-hidden backdrop-blur-sm hover:shadow-3xl hover:shadow-black/10 transition-all duration-500"
    >
      {/* Gradient overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/5 pointer-events-none" />
      
      {/* Premium badge */}
      <div className="absolute top-4 right-4 z-10">
        <div className={`bg-gradient-to-r ${program.badgeColor} text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg`}>
          {program.badge}
        </div>
      </div>

      {/* Image section with overlay */}
      <div className="relative overflow-hidden rounded-t-3xl">
        <motion.div
          variants={imageVariants}
          className="relative"
        >
          <img
            src={program.image}
            alt={`${program.name} cover`}
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
        </motion.div>
        
        {/* Floating stats */}
        <div className="absolute bottom-4 left-4 right-4 flex gap-2">
          <div className="bg-white/90 backdrop-blur-md rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1">
            <Users className="w-3 h-3 text-blue-600" />
            {program.enrolled}+ enrolled
          </div>
          <div className="bg-white/90 backdrop-blur-md rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1">
            <Award className="w-3 h-3 text-yellow-600" />
            {program.rating} rating
          </div>
        </div>
      </div>

      {/* Content section */}
      <div className="flex-1 p-6 space-y-6">
        {/* Title */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900 group-hover:text-purple-700 transition-colors duration-300">
            {program.name}
          </h1>
          <p className="text-gray-600 text-sm">
            {program.description}
          </p>
        </div>

        {/* Duration and Mode */}
        {/* <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">Duration</span>
            </div>
            <p className="font-bold text-blue-900">{program.duration}</p>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4 border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-green-600" />
              <span className="text-xs font-medium text-green-700">Mode</span>
            </div>
            <p className="font-bold text-green-900">{program.mode}</p>
          </div>
        </div> */}

        {/* Program Objectives */}
        {/* <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 border border-purple-200">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-semibold text-purple-700">Program Objectives</span>
          </div>
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-900">Placement within 90 days</h2>
            <div className="bg-purple-600 text-white rounded-full p-1">
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-purple-600">
            <Calendar className="w-3 h-3" />
            <span>Next batch starts in 5 days</span>
          </div>
        </div> */}

        {/* Industry Coverage */}
        {/* <div className="space-y-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Industry Coverage</h3>
            <p className="text-sm text-gray-600">Opportunities across all major sectors</p>
          </div>
          
          <div className="grid grid-cols-5 gap-3">
            {icons?.map((industry, index) => (
              <motion.div
                key={index}
                variants={iconVariants}
                whileHover="hover"
                className="relative group/icon"
              >
                <div className={`w-12 h-12 ${industry.color} rounded-2xl flex items-center justify-center text-white text-lg shadow-lg group-hover/icon:shadow-xl transition-all duration-300`}>
                  {industry.image}
                </div>
                <div className="absolute z-20 bottom-14 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 opacity-0 group-hover/icon:opacity-100 transition-all duration-300 whitespace-nowrap shadow-xl">
                  {industry.name}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div> */}

        {/* CTA Buttons */}
        <div className="space-y-3">
           <button
              onClick={() => {
                navigate(`${program.path}`);
                window.scrollTo({
                  top:0,
                  behavior: "smooth"
                })
              }}
              className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-2xl p-4 text-lg font-semibold hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 flex items-center justify-center gap-2"
            >
              Explore Program
              <ArrowRight className="w-5 h-5" />
            </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProgramsCard;